<template>
  <div class="discovery-feed">
    <div class="container">
      <div v-if="show_create_post_button" class="actions-header">
        <router-link
          :class="{ showFixedButton: showFixedButton }"
          :to="'/plus/create-post'"
          class="button green"
          tag="button"
        >
          create post
        </router-link>
        <bulletin-button v-if="showFixedButton" class="opacity-0"> space </bulletin-button>
      </div>
      <div v-else class="py-30" />
      <div v-if="posts" class="postsContainer">
        <div
          v-for="(post, index) in posts.edges"
          :key="post.node.id"
          v-observe-visibility="{
            callback: (isVisible, entry) => visibilityChanged(isVisible, entry, index),
            once: true,
            throttle: 1000,
          }"
          class="post"
        >
          <div class="brandInfoColumn">
            <div :class="{ isBrand: post.node.brand }" class="brandInfoHeader">
              <div class="brandImageContainer">
                <img
                  v-if="post.node.brand && post.node.brand.thumbnailImageUrl != null"
                  v-trackevent="{
                    action: 'Brand Profile Image Click',
                    properties: {
                      brand: post.node.brand.companyName,
                    },
                  }"
                  :alt="post.node.brand.vanityUrlName"
                  :src="
                    $utils.getCDNURL({
                      src: post.node.brand.thumbnailImageUrl,
                      h: 80,
                      w: 69,
                      q: 80,
                    })
                  "
                  @click="handlebrandImageClick(post.node.brand.vanityUrlName)"
                />
                <img v-else alt="" src="@/assets/img/bulletin-admin-thumbnail.png" srcset="" />
              </div>
              <div class="brandNameContainer">
                <span
                  v-if="post.node.brand"
                  v-trackevent="{
                    action: 'Brand Profile Image Click',
                    properties: {
                      brand: post.node.brand.companyName,
                    },
                  }"
                  @click="handlebrandNameClick(post.node.brand.vanityUrlName)"
                >
                  {{ post.node.brand.companyName }}
                </span>
                <template v-else> Bulletin </template>
              </div>
            </div>
            <div v-if="post.node.brand && isRetailer">
              <chat-button-by-id
                :brand-id="post.node.brand.id"
                :class="'messageButton button sml black'"
                @handle-click="messageButtonClicked(index)"
              >
                <inline-svg :src="require(`../../assets/img/icon-mail.svg`)" height="20" width="20" />
                <span class="button-content"> message </span>
              </chat-button-by-id>
              <follow-button :brand-id="post.node.brand.id" @handle-click="followButtonClicked(index)" />
            </div>
            <brand-values-list v-if="post.node.brand" :brand-list="post.node.brand.values" />
          </div>
          <div class="postContentColumn">
            <div class="postImageContainer">
              <post-image-with-product-carousel
                :admin-link="post.node.link"
                :img-id="post.node.id"
                :img-src="$utils.optimizedFeedImageUrl(post.node.media.croppedUrl)"
                :post-index="index"
                :product-tags="post.node.media.productTags"
                @product-tags-revealed="productTagsRevealed"
                @product-tag-cta-clicked="productTagCTAClicked"
              />
            </div>
            <div class="postDescription">
              <post-actions :post="post" @want-post="wantPost(index)" @unwant-post="unWantPost(index)" />
              <div class="timestamp-container">
                {{
                  -(new Date().getTime() - new Date(post.node.createdAt * 1000).getTime()) | duration("humanize", true)
                }}
              </div>
              <post-caption :copy="post.node.postContent.body" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="preloadScreen">
        <div v-if="gqlError">Error</div>
        <img v-else alt="bulletin logo" class="loadingImage" src="@/assets/img/bulletin-admin-thumbnail.png" />
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-underscore-dangle */
import InlineSvg from "vue-inline-svg";
import gql from "graphql-tag";
import FeedEvents from "@/services/feed/events";
import Vue from "vue";
import { ObserveVisibility } from "vue-observe-visibility";
import FollowButton from "./TheFollowButton.vue";
import PostCaption from "./PostCaption.vue";
import ChatButtonById from "../../components/shared/chatButtonById/chatButtonById.vue";
import FavoritesApi from "../../services/api/favoritesApi";
import PostActions from "@/views/TheDiscoveryFeed/PostActions";
import BrandValuesList from "@/views/TheDiscoveryFeed/BrandValuesList";
import PostImageWithProductCarousel from "@/views/TheDiscoveryFeed/PostImageWithProductCarousel/PostImageWithProductCarousel";

const FavoritesApiService = new FavoritesApi();

Vue.directive("observe-visibility", ObserveVisibility);

export default {
  components: {
    PostImageWithProductCarousel,
    ChatButtonById,
    InlineSvg,
    FollowButton,
    PostCaption,
    PostActions,
    BrandValuesList,
  },
  apollo: {
    posts: {
      query: gql`
        query ($id: String) {
          posts(filters: { id: $id }) {
            edges {
              node {
                id
                link
                postContent
                createdAt
                isLiked
                postType
                createdAt
                userId
                brand {
                  thumbnailImageUrl
                  companyName
                  id
                  values
                  vanityUrlName
                }
                media {
                  id
                  originalUrl
                  croppedUrl
                  cropParams
                  productTags
                }
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
        };
      },
      error() {
        this.$swal("Something Went Wrong", "Please check the URL and try again.");
        this.gqlError = true;
      },
    },
  },
  data() {
    return {
      isRetailer: false,
      isVisitor: false,
      show_create_post_button: false,
      posts: null,
      show_loader: false,
      buffered_events: [],
      feed_events: undefined,
      showFixedButton: false,
      showNewPost: false,
      gqlError: false,
    };
  },
  beforeMount() {
    if (this.$session.getUserInfo() === null) {
      this.isVisitor = true;
    } else {
      this.isRetailer = this.$session.getUserInfo().role === "RETAILER";
      this.show_create_post_button = !this.isRetailer;

      if (this.isRetailer) {
        FavoritesApiService.getFavoriteBrands().then(({ data }) => {
          this.$store.commit("setFavoriteState", data.brand_ids);
        });
      }
    }
  },
  created() {
    this.$data.feed_events = new FeedEvents(this);
  },
  mounted() {
    this.$ma.trackEvent({
      action: "Bulletin Plus Single Post Page View",
    });
  },
  methods: {
    wantPost,
    unWantPost,
    visibilityChanged,
    viewedAPost,
    productTagsRevealed,
    productTagCTAClicked,
    messageButtonClicked,
    followButtonClicked,
    handlebrandImageClick,
    handlebrandNameClick,
    redirectToBrandPage,
  },
};
function followButtonClicked(index) {
  if (this.isVisitor) {
    return;
  }
  const post = this.$data.posts.edges[index].node;
  this.$data.feed_events.track("FOLLOW_BRAND", post, index);
}
function messageButtonClicked(index) {
  if (this.isVisitor) {
    return;
  }
  const post = this.$data.posts.edges[index].node;
  this.$data.feed_events.track("MESSAGE_BRAND", post, index);
}
function productTagCTAClicked(postIndex, tagIndex) {
  const post = this.$data.posts.edges[postIndex].node,
    tag = post.media.productTags[tagIndex];
  if (this.isVisitor) {
    return;
  }
  this.$data.feed_events.track(
    "PRODUCT_TAG_CTA_CLICKED",
    {
      ...post,
      index: postIndex,
    },
    {
      tag_type: tag.tag_type,
      // eslint-disable-next-line no-prototype-builtins
      parent_category: tag.hasOwnProperty("parent_category") ? tag.parent_category : "",
    }
  );
}
function productTagsRevealed(postIndex, tagIndex) {
  const post = this.$data.posts.edges[postIndex].node,
    tag = post.media.productTags[tagIndex];
  if (this.isVisitor) {
    return;
  }
  this.$data.feed_events.track(
    "PRODUCT_TAG_VIEWED",
    {
      ...post,
      index: postIndex,
    },
    {
      tag_type: tag.tag_type,
      // eslint-disable-next-line no-prototype-builtins
      parent_category: tag.hasOwnProperty("parent_category") ? tag.parent_category : "",
    }
  );
}
function wantPost(index) {
  if (this.isVisitor) {
    this.$eventBus.$emit("apply-modal-unregistered", this.data);
    return;
  }
  const post = this.$data.posts.edges[index].node;
  post.isLiked = true;
  this.$data.feed_events.track("LIKE", post, index);
}
function unWantPost(index) {
  const post = this.$data.posts.edges[index].node;
  post.isLiked = false;
  if (this.isVisitor) {
    return;
  }
  this.$data.feed_events.track("DISLIKE", post, index);
}
function viewedAPost(post, index) {
  if (this.isVisitor) {
    return;
  }
  this.$data.feed_events.track("VIEW", post, index);
}
function visibilityChanged(isVisible, elem, index) {
  if (isVisible) {
    const post = this.$data.posts.edges[index].node;
    this.viewedAPost(post, index);
  }
}
function handlebrandImageClick(id) {
  this.redirectToBrandPage(id);
}
function handlebrandNameClick(id) {
  this.redirectToBrandPage(id);
}
function redirectToBrandPage(vanityUrlName) {
  this.$router.push("/brands/" + vanityUrlName);
}
</script>
